import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import {
  Briefcase,
  ScrollText,
  LayoutDashboard,
  Bell,
  Sparkles,
  ShieldCheck,
  Users,
  User as UserIcon,
  Sun,
  Moon,
  Monitor,
  LogOut,
  Plus,
  Settings,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const ROLES_HIDE_PALETTE_SEARCH = new Set(["read_only_leadership"]);

function useDebounce(value, delay = 200) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ cases: [], team_members: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setPreference } = useTheme();
  const debouncedQuery = useDebounce(query, 200);

  // ⌘K / Ctrl+K to open
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => {
          const next = !o;
          if (next) {
            // Phase 12.1 — broadcast (synchronous) so the topbar hint pill can dismiss itself
            // We schedule it on the macrotask queue to avoid React 18 batching
            // around the state updater function.
            queueMicrotask(() =>
              window.dispatchEvent(new CustomEvent("cmdk:opened"))
            );
          }
          return next;
        });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Reset query when closing
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults({ cases: [], team_members: [] });
    }
  }, [open]);

  // Search debounced
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!open || q.length < 2 || !user || ROLES_HIDE_PALETTE_SEARCH.has(user.role)) {
      setResults({ cases: [], team_members: [] });
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/search?q=${encodeURIComponent(q)}&limit=10`)
      .then((r) => {
        if (!cancelled) setResults(r.data || { cases: [], team_members: [] });
      })
      .catch(() => {
        if (!cancelled) setResults({ cases: [], team_members: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open, user]);

  const go = useCallback(
    (path) => {
      setOpen(false);
      navigate(path);
    },
    [navigate]
  );

  const role = user?.role;
  const canSeeCompliance = role === "compliance" || role === "legal_review";
  const canSeeAudit = role === "hr" || role === "compliance" || role === "legal_review";
  const canSeeAdmin = role === "hr" && user?.is_admin;
  const canSeeCases = role !== "read_only_leadership";

  const navItems = useMemo(() => {
    const out = [
      { id: "nav-dashboard", label: "Dashboard", path: "/dashboard", Icon: LayoutDashboard },
    ];
    if (canSeeCases) out.push({ id: "nav-cases", label: "Cases", path: "/cases", Icon: Briefcase });
    out.push({ id: "nav-insights", label: "Insights", path: "/insights", Icon: Sparkles });
    out.push({ id: "nav-notifications", label: "Notifications", path: "/notifications", Icon: Bell });
    if (canSeeCases) out.push({ id: "nav-team-members", label: "Team Members", path: "/team-members", Icon: Users });
    if (canSeeAudit) out.push({ id: "nav-audit", label: "Audit Log", path: "/audit-logs", Icon: ScrollText });
    if (canSeeCompliance) out.push({ id: "nav-compliance", label: "Compliance", path: "/compliance", Icon: ShieldCheck });
    if (canSeeAdmin) out.push({ id: "nav-admin", label: "Admin", path: "/admin", Icon: Settings });
    out.push({ id: "nav-profile", label: "Profile", path: "/profile", Icon: UserIcon });
    return out;
  }, [canSeeCases, canSeeAudit, canSeeCompliance, canSeeAdmin]);

  const showResults = debouncedQuery.trim().length >= 2;
  const totalResults =
    (results.cases?.length || 0) + (results.team_members?.length || 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="command-palette"
        className="overflow-hidden p-0 sm:max-w-[600px]"
        style={{
          backgroundColor: "var(--surface)",
          border: "1px solid var(--border-subtle)",
          boxShadow: "var(--shadow-modal)",
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        >
          <Command
            shouldFilter={false}
            className="bg-transparent"
            data-testid="command-palette-inner"
          >
            <CommandInput
              data-testid="command-palette-input"
              placeholder="Search cases, team members, or jump to…"
              value={query}
              onValueChange={setQuery}
              className="text-sm"
            />
            <CommandList className="max-h-[480px]">
              {!showResults ? (
                <>
                  <CommandGroup heading="Navigate">
                    {navItems.map((it) => (
                      <CommandItem
                        key={it.id}
                        data-testid={`cmd-${it.id}`}
                        value={it.label}
                        onSelect={() => go(it.path)}
                        className="text-sm"
                      >
                        <it.Icon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                        {it.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup heading="Actions">
                    {(role === "hr") && (
                      <CommandItem
                        data-testid="cmd-action-new-case"
                        value="New case"
                        onSelect={() => go("/cases?new=1")}
                        className="text-sm"
                      >
                        <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
                        New case
                      </CommandItem>
                    )}
                    <CommandItem
                      data-testid="cmd-action-theme-light"
                      value="Switch to light theme"
                      onSelect={() => {
                        setPreference("light");
                        setOpen(false);
                      }}
                      className="text-sm"
                    >
                      <Sun className="mr-2 h-4 w-4" strokeWidth={1.5} />
                      Switch to light theme
                    </CommandItem>
                    <CommandItem
                      data-testid="cmd-action-theme-dark"
                      value="Switch to dark theme"
                      onSelect={() => {
                        setPreference("dark");
                        setOpen(false);
                      }}
                      className="text-sm"
                    >
                      <Moon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                      Switch to dark theme
                    </CommandItem>
                    <CommandItem
                      data-testid="cmd-action-theme-system"
                      value="Follow system theme"
                      onSelect={() => {
                        setPreference("system");
                        setOpen(false);
                      }}
                      className="text-sm"
                    >
                      <Monitor className="mr-2 h-4 w-4" strokeWidth={1.5} />
                      Follow system theme
                    </CommandItem>
                    <CommandItem
                      data-testid="cmd-action-logout"
                      value="Sign out"
                      onSelect={() => {
                        setOpen(false);
                        logout?.();
                      }}
                      className="text-sm"
                    >
                      <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} />
                      Sign out
                    </CommandItem>
                  </CommandGroup>
                </>
              ) : (
                <>
                  {loading && (
                    <div
                      data-testid="cmd-loading"
                      className="px-4 py-3 text-xs text-[color:var(--page-fg-muted)]"
                    >
                      Searching…
                    </div>
                  )}
                  {!loading && totalResults === 0 && (
                    <CommandEmpty
                      data-testid="cmd-no-results"
                      className="py-8 text-center text-sm text-[color:var(--page-fg-muted)]"
                    >
                      No matches for &ldquo;{debouncedQuery}&rdquo;
                    </CommandEmpty>
                  )}
                  {results.cases?.length > 0 && (
                    <CommandGroup heading="Cases">
                      {results.cases.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.case_id} ${c.team_member_name || ""} ${c.specific_issue || ""}`}
                          onSelect={() => go(`/cases/${c.id}`)}
                          className="text-sm"
                          data-testid={`cmd-case-${c.id}`}
                        >
                          <Briefcase className="mr-2 h-4 w-4" strokeWidth={1.5} />
                          <span className="font-mono text-xs text-[color:var(--page-fg)] dark:text-slate-100">
                            {c.case_id}
                          </span>
                          {c.team_member_name && (
                            <span className="ml-2 text-xs text-[color:var(--page-fg-muted)]">
                              · {c.team_member_name}
                            </span>
                          )}
                          {c.service_line && (
                            <span className="ml-auto rounded-md border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[color:var(--page-fg-muted)] hairline">
                              {c.service_line}
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {results.team_members?.length > 0 && (
                    <CommandGroup heading="Team Members">
                      {results.team_members.map((t) => (
                        <CommandItem
                          key={t.id}
                          value={`${t.name} ${t.position_id || t.file_number || ""}`}
                          onSelect={() =>
                            t.latest_case_uuid
                              ? go(`/cases/${t.latest_case_uuid}`)
                              : go(`/team-members?q=${encodeURIComponent(t.name)}`)
                          }
                          className="text-sm"
                          data-testid={`cmd-tm-${t.id}`}
                        >
                          <UserIcon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                          {t.name}
                          <span className="ml-2 font-mono text-[11px] text-[color:var(--page-fg-muted)]">
                            #{t.position_id || t.file_number}
                          </span>
                          {t.latest_case_id && (
                            <span className="ml-auto font-mono text-[10px] text-[color:var(--page-fg-muted)]">
                              {t.latest_case_id}
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
            <div
              className="flex items-center justify-between border-t px-3 py-2 text-[10px] uppercase tracking-wider text-[color:var(--page-fg-muted)]"
              style={{ borderColor: "var(--border-subtle)" }}
            >
              <span>
                <span className="kbd mr-1">↑</span>
                <span className="kbd mr-1">↓</span>
                navigate
                <span className="kbd ml-3 mr-1">↵</span>
                select
                <span className="kbd ml-3 mr-1">esc</span>
                close
              </span>
              <span className="text-[color:var(--page-fg-muted)]">Goodwin Resolve</span>
            </div>
          </Command>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}

// Tiny helper component — Cmd+K hint pill rendered in the topbar.
export function CmdKHint({ onClick }) {
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="cmdk-hint"
      aria-label="Open command palette"
      className="hidden h-9 items-center gap-2 rounded-lg border px-3 text-xs text-[color:var(--page-fg-muted)] transition hover:border-brand-strong hover:text-[color:var(--page-fg)] sm:inline-flex dark:hover:text-slate-100"
      style={{ borderColor: "var(--border-strong)", backgroundColor: "var(--surface-2)" }}
    >
      Search…
      <span className="ml-1 flex items-center gap-1">
        <span className="kbd">{isMac ? "⌘" : "Ctrl"}</span>
        <span className="kbd">K</span>
      </span>
    </button>
  );
}
